import Form from "@/types/Form.interface"
import FormSubmission from "@/types/FormSubmission.interface"
import Pagination from "@/types/Pagination.interface"
import { FormSubmissionParams } from "@/types/PaginationParams.interface"

interface State {
    formsViewForms: Form[]
    formsViewSelectedForm: Form
    formsViewSubmissions: FormSubmission[]
    formsViewSubmissionsCurrentPage: number
    formsViewSubmissionsLastPage: number
    formsViewSubmissionsNextPage: number
    formsViewSubmissionsPaginating: boolean
    formsViewSubmissionsParams: FormSubmissionParams
}

const defaults = () => {
    return {
        formsViewForms: [],
        formsViewSelectedForm: null,
        formsViewSubmissions: [],
        formsViewSubmissionsCurrentPage: null,
        formsViewSubmissionsLastPage: null,
        formsViewSubmissionsNextPage: null,
        formsViewSubmissionsPaginating: false,
        formsViewSubmissionsParams: { page: 1, limit: 30 }
    }
}

export default {
    state: defaults(),
    mutations: {
        //  FORMS
        formsViewFormsPush (state: State, forms: Form[]): void {
            //  Update our list
            state.formsViewForms = window.removeDuplicatesById(state.formsViewForms, forms)

            //  Get the currently selected form
            const selectedForm = state.formsViewSelectedForm

            //  If the form does NOT exist, we have nothing to do
            if (!selectedForm) return

            //  Else, find the selected form in the array of updated forms
            const updatedForm = forms.find((x: Form) => x.id === selectedForm.id)

            //  If the selected form was NOT updated, we have nothing to do
            if (!updatedForm) return

            //  Else, update the selected form
            state.formsViewSelectedForm = updatedForm
        },
        formsViewFormsRemove (state: State, ids: number[]): void {
            //  Get our current lists of forms
            const forms = state.formsViewForms

            //  If no forms, do nothing
            if (!forms) return

            //  Else, filter out our removed ids
            const filtered = forms.filter((x: Form) => !ids.includes(x.id))

            //  Set our forms to the filtered list
            state.formsViewForms = filtered

            //  Get the currently selected form
            const selectedForm = state.formsViewSelectedForm

            //  If the form does NOT exist, we have nothing to do
            if (!selectedForm) return

            //  Else, check if this form was deleted
            const deleted = ids.find((x: number) => x === selectedForm.id)

            //  If the selected form was NOT found, then do nothing
            if (!deleted) return

            //  Else, set our selected form to null
            state.formsViewSelectedForm = null
        },
        formsViewFormsSet (state: State, forms: Form[]): void {
            state.formsViewForms = forms
        },
        //  FORM SUBMISSIONS
        formsViewSubmissionsPush (state: State, submissions: FormSubmission[]): void {
            state.formsViewSubmissions = window.removeDuplicatesById(state.formsViewSubmissions, submissions)
        },
        formsViewSubmissionsRemove (state: State, ids: number[]): void {
            //  Get our current lists of submissions
            const submissions = state.formsViewSubmissions

            //  If no submissions, do nothing
            if (!submissions) return

            //  Else, filter out our removed ids
            const filtered = submissions.filter((x: FormSubmission) => !ids.includes(x.id))

            //  Replace with the filtered list
            state.formsViewSubmissions = filtered
        },
        formsViewSubmissionsSync (state: State, updates: FormSubmission[]): void {
            //  Get our list of submissions
            const submissions = state.formsViewSubmissions

            //  If no submissions, do nothing
            if (!submissions) return

            //  Grab all the ids from our submissions
            const ids = submissions.map((x: FormSubmission) => x.id)

            //  Else, filter out our updates to ONLY contain items that are in the submissions list
            const filtered = updates.filter((x: FormSubmission) => ids.includes(x.id))

            //  We also need to find which submissions we have to remove from the list
            const remove = window._.differenceBy(updates, filtered, 'id')

            //  Update our list with the filtered array
            state.formsViewSubmissions = window.removeDuplicatesById(state.formsViewSubmissions, filtered, remove)
        },
        formsViewSubmissionsSet (state: State, submissions: FormSubmission[]): void {
            state.formsViewSubmissions = submissions
        },
        formsViewSubmissionsPaginationPush (state: State, page: Pagination): void {
            //  If this is page one, reset our submissions list
            if (page.current_page === 1) state.formsViewSubmissions = []

            state.formsViewSubmissionsCurrentPage = page.current_page
            state.formsViewSubmissionsLastPage = page.last_page
            state.formsViewSubmissionsNextPage = page.current_page + 1
        },
        formsViewSubmissionsPaginatingSet (state: State, bool: boolean): void {
            state.formsViewSubmissionsPaginating = bool
        },
        formsViewSubmissionsParamsSet (state: State, params: FormSubmissionParams): void {
            state.formsViewSubmissionsParams = params
        },
        //  SELECTED FORM
        formsViewSelectedFormSet (state: State, form: Form): void {
            state.formsViewSelectedForm = form
        },
        formsViewReset (state: State): void {
            Object.assign(state, defaults())
        }
    },
    actions: {
        formsViewSelectedFormGet ({ commit, getters }, formId: number): Promise<void> {
            //  Grab our global forms dataset
            const forms = getters.forms

            //  Check if our form we are searching for already exists
            const existingForm = forms.find((x: Form) => x.id === formId)

            //  If the form was found, return it
            if (existingForm) return commit('formsViewSelectedFormSet', existingForm)

            //  Else, we have to fin dthis form so assemble our API endpoint
            const url = `${getters.api}/forms/${formId}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Set our selected form
                commit('formsViewSelectedFormSet', response.data)

                //  Update the global dataset
                commit('formsPush', [response.data])
            }).catch(() => null)
        },
        formsViewSelectedFormSubmissionsGet ({ commit, getters }, formId: number): Promise<void> {
            //  Get the global form submissions list
            const submissions = getters.formSubmissions

            //  Search for the existing submissions in the global dataset
            const formSubmissions = submissions[formId]

            //  If the form's submissions were found, return them
            if (formSubmissions) return commit('formsViewSubmissionsSet', formSubmissions)

            //  Grab our pagination params
            const p = getters.formsViewSubmissionsParams

            //  Attach our form id
            p.form_id = formId

            //  Start our pagination tracker
            commit('formsViewSubmissionsPaginatingSet', true)

            //  Assemble the API endpoint
            const url = `${getters.api}/form-submissions?${window.toUrlParams(p)}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Update pagination params
                commit('formsViewSubmissionsPaginationPush', response.data)

                //  Set the forms view selected form submissions
                commit('formsViewSubmissionsPush', response.data.data)

                //  This workflow is used so we can set a static list of submissions and never have to paginate again :)
                //  Grab our current and last page
                const current = response.data.current_page
                const last = response.data.last_page

                //  If these pages are equal, commit our current submissions to global data set
                if (current === last) commit('formSubmissionsSet', getters.formsViewSubmissions)

                //  Stop the pagination tracker
                commit('formsViewSubmissionsPaginatingSet', false)
            }).catch(() => null)
        }
    },
    getters: {
        formsViewForms (state: State): Form[] {
            return state.formsViewForms
        },
        formsViewSelectedForm (state: State): Form {
            return state.formsViewSelectedForm
        },
        formsViewSubmissions (state: State): FormSubmission[] {
            return state.formsViewSubmissions
        },
        formsViewSubmissionsCurrentPage (state: State): number {
            return state.formsViewSubmissionsCurrentPage
        },
        formsViewSubmissionsLastPage (state: State): number {
            return state.formsViewSubmissionsLastPage
        },
        formsViewSubmissionsNextPage (state: State): number {
            return state.formsViewSubmissionsNextPage
        },
        formsViewSubmissionsPaginating (state: State): boolean {
            return state.formsViewSubmissionsPaginating
        },
        formsViewSubmissionsParams (state: State): FormSubmissionParams {
            return state.formsViewSubmissionsParams
        }
    }
}
